<template>
    <div class="charts_row">
        <article class="chart_card p-3">
            <div class="chart_canvas">
                <!-- Toolbar superpuesta arriba a la derecha, en la MISMA fila que
                    la leyenda. La leyenda de ECharts reserva su ancho con
                    `legend.width` (no `right`, que el modo scroll ignora) para que
                    cada uno respete su espacio sin solaparse. -->
                <div class="chart_toolbar">
                    <button type="button"
                        class="tooltip_toggle detail_toggle"
                        :class="{ 'is-active': showStackedTooltip }"
                        :aria-pressed="String(showStackedTooltip)"
                        :title="showStackedTooltip ? 'Ocultar detalle' : 'Mostrar detalle'"
                        @click="showStackedTooltip = !showStackedTooltip">
                        <span class="dot" aria-hidden="true"></span>
                        <span>Detalle</span>
                    </button>
                </div>
                <p v-if="error" class="chart_state chart_state--error">
                    {{ error }}
                </p>
                <p v-else-if="isLoading" class="chart_state">Cargando…</p>
                <p v-else-if="!hasStackedData" class="chart_state">
                    Sin datos de clientes para el período seleccionado.
                </p>
                <SalesClientsStackedChart v-else
                    :month-headers="chartsData.monthHeaders"
                    :series="chartsData.byClient"
                    :show-tooltip="showStackedTooltip" />
            </div>
        </article>

        <article class="chart_card p-3">
            <div class="chart_canvas">
                <div class="chart_toolbar">
                    <!-- "% de ventas" a la izquierda de "Detalle": toggle de
                        representación (montos absolutos ↔ share de cada cliente
                        sobre el total del período). Sólo en el chart de líneas.
                        `showPercentToggle` permite ocultarlo (Gastos lo activará
                        cuando su tabla soporte el modo %). -->
                    <button v-if="showPercentToggle" type="button"
                        class="tooltip_toggle percent_toggle"
                        :class="{ 'is-active': percentMode }"
                        :aria-pressed="String(percentMode)"
                        :title="percentMode ? 'Ver montos' : 'Ver % de ventas'"
                        @click="$emit('update:percent-mode', !percentMode)">
                        <span class="dot" aria-hidden="true"></span>
                        <span>% de ventas</span>
                    </button>
                    <button type="button"
                        class="tooltip_toggle detail_toggle"
                        :class="{ 'is-active': showLineTooltip }"
                        :aria-pressed="String(showLineTooltip)"
                        :title="showLineTooltip ? 'Ocultar detalle' : 'Mostrar detalle'"
                        @click="showLineTooltip = !showLineTooltip">
                        <span class="dot" aria-hidden="true"></span>
                        <span>Detalle</span>
                    </button>
                </div>
                <p v-if="error" class="chart_state chart_state--error">
                    {{ error }}
                </p>
                <p v-else-if="isLoading" class="chart_state">Cargando…</p>
                <p v-else-if="!hasStackedData" class="chart_state">
                    Sin datos de clientes para el período seleccionado.
                </p>
                <SalesClientsLineChart v-else
                    :month-headers="chartsData.monthHeaders"
                    :series="chartsData.byClient"
                    :show-tooltip="showLineTooltip"
                    :percent-mode="percentMode"
                    :percent-base="percentBase"
                    :total-label="totalLabel" />
            </div>
        </article>
    </div>
</template>

<script>
import SalesClientsStackedChart from "@/components/financialReports/charts/SalesClientsStackedChart.vue";
import SalesClientsLineChart from "@/components/financialReports/charts/SalesClientsLineChart.vue";

// Row de los dos gráficos del header del tab Ventas. Maneja loading/error/
// empty states y delega el rendering a los dos componentes de chart, cada
// uno sobre su propia card.
//
// Decisión: el container conoce el shape de `chartsData` (lo provee el store)
// y reparte slices a cada chart. Los charts no importan Vuex — reciben arrays
// por prop. Mismo patrón que BodySalesAnalysis con sus cards.

export default {
    name: "SalesChartsRow",

    components: { SalesClientsStackedChart, SalesClientsLineChart },

    props: {
        // El payload normalizado del store (`salesReport.chartsData`):
        //   { monthHeaders, byClient[], clientCounts[], avgTickets[],
        //     hasOtros, topN, periodRange }.
        // `null` antes del primer fetch — el row renderea loading/empty.
        chartsData: { type: Object, default: null },
        isLoading: { type: Boolean, default: false },
        error: { type: String, default: "" },
        // % de ventas: gobernado por el padre (`:percent-mode.sync`) porque el
        // mismo toggle también cambia la tabla principal a porcentajes. El botón
        // de este row emite `update:percent-mode`; el line chart lo consume.
        percentMode: { type: Boolean, default: false },
        // Muestra/oculta el toggle "% de ventas". Default true (Ventas). Gastos
        // lo apaga hasta que su tabla soporte el modo porcentaje.
        showPercentToggle: { type: Boolean, default: true },
        // Ventas netas por período (map header → monto), denominador del % en
        // percentMode para Costos/Gastos — el mismo que la fila "% Costos/Ventas"
        // de la tabla. `null` (Ventas) → el line chart cae a la suma de series.
        salesByPeriod: { type: Object, default: null },
        // Etiqueta del total al pie del tooltip en percentMode. Default
        // "Total ventas" (Ventas); breakdown pasa "Total costos"/"Total gastos".
        totalLabel: { type: String, default: "Total ventas" },
    },

    data() {
        return {
            // Estado local de cada toggle de tooltip. No vive en el store: es
            // preferencia visual del row, no estado del dominio. Ambos charts
            // (stacked + líneas) consumen la misma serie por cliente.
            showStackedTooltip: true,
            showLineTooltip: true,
        };
    },

    computed: {
        // Ambos charts del row usan `byClient` — un solo guard de datos sirve
        // para los dos.
        hasStackedData() {
            return Boolean(
                this.chartsData
                && this.chartsData.monthHeaders.length > 0
                && this.chartsData.byClient.length > 0,
            );
        },

        // Denominador del % del line chart en percentMode, alineado a los
        // headers del chart: ventas netas por período. `null` (Ventas, sin
        // `salesByPeriod`) deja que el chart use la suma de sus series. Header
        // sin venta registrada → `null` (línea cortada en ese punto).
        percentBase() {
            if (!this.salesByPeriod || !this.chartsData) return null;
            return this.chartsData.monthHeaders.map((header) => {
                const value = this.salesByPeriod[header];
                return typeof value === "number" ? value : null;
            });
        },
    },
};
</script>

<style scoped lang="scss">
.charts_row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
}

// Misma surface treatment que StoresDistributionCard — surface blanca + border
// suave + radius 16 + whisper shadow. Los charts adentro se vuelven transparent
// para reusar este chrome.
.chart_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 16px;
    box-shadow: var(--report-shadow-card);
    height: 340px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

// Toolbar superpuesta arriba a la derecha del canvas, en la misma fila que la
// leyenda. Agrupa los toggles (Detalle, y en el line chart también
// "% de ventas"). La leyenda reserva su ancho vía `legend.width` para no
// solaparse (ver charts).
.chart_toolbar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    display: flex;
    gap: 6px;
}

// Toggle pill: mismo léxico visual que .filter_btn del TabHeader (border-radius
// 8 para integrarlo con la chrome de cards). Dot a la izquierda actúa como
// indicador de estado: rellena cuando is-active, outline cuando off.
.tooltip_toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: transparent;
    border: 1px solid var(--report-border);
    border-radius: 8px;
    color: var(--report-text-muted);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease;

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 1px solid currentColor;
        background: transparent;
        transition: background-color 160ms ease;
    }

    &:hover {
        background: var(--report-surface);
        color: var(--report-text);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
    }

    &.is-active {
        background: var(--report-accent-bg);
        border-color: var(--report-accent-border);
        color: var(--report-accent-text);

        .dot {
            background: currentColor;
        }
    }
}

.chart_canvas {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    position: relative;
}

.chart_state {
    margin: auto;
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;

    &--error {
        color: var(--report-negative);
    }
}

// Stack vertical en viewport angosto. Subido a 1280px (antes 1024): en el modo
// 2-columnas cada chart necesita ancho para leyenda + toggles en la misma fila
// sin solaparse. Por debajo de 1280 los charts van a una columna (full-width),
// donde la leyenda respira y casi no pagina.
@media (max-width: 1280px) {
    .charts_row {
        grid-template-columns: 1fr;
    }
}
</style>
